import React, { useState } from 'react';

import styled from 'styled-components';

import CloseIcon from 'common/Icons/CloseIcon';
import ConfirmModal from 'common/Modal/ConfirmModal';
import Button from 'components/lib/Button';

import UserManagementEmptyState from './UserManagementEmptyState';

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1;
  gap: 10px;
  height: 100%;
  overflow-y: auto;
`;

const ListItem = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border: 1px solid var(--line-gray);
  padding: 14px 20px;
  border-radius: 4px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
`;

const ListItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
`;

const CommunityName = styled.span`
  font-size: 14px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const CommunityCompany = styled.span`
  font-size: 12px;
  color: var(--text-primary);
`;

export type UserManagementCommunityOption = {
  id: number;
  comunity_name: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  company?: { id: number; name: string | null } | null;
};

const formatCommunityAddress = (community: UserManagementCommunityOption) => {
  if (community.address) {
    return community.address;
  }

  return [community.city, community.state, community.zip_code].filter(Boolean).join(', ') || '-';
};

const formatCommunityDetails = (community: UserManagementCommunityOption, companyName?: string | null) => {
  const address = formatCommunityAddress(community);
  const details = [companyName, address].filter(Boolean);

  return details.length ? details.join(' · ') : '-';
};

type Props = {
  communities: UserManagementCommunityOption[];
  fallbackCompanyName?: string | null;
  onChange: (communities: UserManagementCommunityOption[]) => void;
};

const UserManagementSelectedCommunities = ({ communities, fallbackCompanyName, onChange }: Props) => {
  const [removeCommunity, setRemoveCommunity] = useState<UserManagementCommunityOption | null>(null);

  return (
    <ListContainer className={'mini-scroll'}>
      {communities.map((community) => (
        <ListItem key={community.id} data-test-id={`user-management-selected-community-${community.id}`}>
          <ListItemInfo>
            <CommunityName>{community.comunity_name}</CommunityName>
            <CommunityCompany>
              {formatCommunityDetails(community, community.company?.name || fallbackCompanyName)}
            </CommunityCompany>
          </ListItemInfo>
          <Button
            shape="circle"
            type={'link'}
            size={'small'}
            icon={<CloseIcon size={14} />}
            onClick={() => setRemoveCommunity(community)}
            data-test-id={`user-management-remove-community-${community.id}`}
          />
        </ListItem>
      ))}

      <ConfirmModal
        open={Boolean(removeCommunity)}
        title={'Remove Community'}
        subtitle={removeCommunity?.comunity_name || ''}
        description={<div>This action will remove selected community.</div>}
        confirmLabel={'Confirm'}
        confirmButtonDataTestId="user-management-remove-community-confirm"
        cancelButtonDataTestId="user-management-remove-community-cancel"
        onConfirm={() => {
          onChange(communities.filter((community) => community.id !== removeCommunity?.id));
          setRemoveCommunity(null);
        }}
        onCancel={() => setRemoveCommunity(null)}
      />

      {communities.length === 0 && (
        <UserManagementEmptyState
          title={'There are no communities.'}
          description={
            <>
              Use the search above to add communities to this user.
              <br />
              Only communities that are not yet selected will appear in the search.
            </>
          }
        />
      )}
    </ListContainer>
  );
};

export default UserManagementSelectedCommunities;
